import React from 'react';
import { Upload, Plus, X, Save } from 'lucide-react';

const WEIGHT_OPTIONS = [
  { value: '100g', label: '100g' },
  { value: '200g', label: '200g' },
  { value: '250g', label: '250g' },
  { value: '300g', label: '300g' },
  { value: '350g', label: '350g' },
  { value: '400g', label: '400g' },
  { value: '500g', label: '500g' },
  { value: '750g', label: '750g' },
  { value: '1kg', label: '1kg' },
];

const AddItemForm = ({
  editingId,
  formData,
  imagePreview,
  loading,
  onInputChange,
  onImageChange,
  onImagesChange,
  onClearCover,
  onSubmit,
  onReset
}) => {
  const handleFormSubmit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };

  return (
    <form onSubmit={handleFormSubmit} className="space-y-6">
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Item Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={onInputChange}
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:outline-none transition-colors placeholder:text-gray-400 placeholder:font-medium text-gray-600"
            placeholder="Croissant, Cake, etc."
            required
          />
        </div>
        
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Category</label>
          <select
            name="categorySelect"
            value={formData.category === "Other" ? "Other" : (formData.category && !["Brownies", "Cakes", "Cheesecakes", "Cookies", " Savoury", " Tub Cakes & Jar Cakes","Tea Cakes"].includes(formData.category) ? "Other" : formData.category)}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "Other") {
                // When "Other" is selected, set category to "Other" to show custom input
                const event = { target: { name: 'category', value: 'Other' } };
                onInputChange(event);
              } else {
                // When a predefined category is selected, set it directly
                const event = { target: { name: 'category', value: value } };
                onInputChange(event);
              }
            }}
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:outline-none transition-colors text-gray-600"
          >
            
            <option value="">Select Category</option>
            <option value="Brownies">Brownies</option>
            <option value="Cakes">Cakes</option>
            <option value="Cheesecakes">Cheesecakes</option>
            <option value="Cookies">Cookies</option>
            <option value="Savoury">Savoury</option>
            <option value="Tub Cakes & Jar Cakes">Tub Cakes & Jar Cakes</option>
            <option value="Tea Cakes">Tea Cakes</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      {/* Custom Category Input */}
      {formData.category === "Other" && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Custom Category</label>
          <input
            type="text"
            name="customCategory"
            value={formData.customCategory || ""}
            onChange={onInputChange}
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:outline-none transition-colors placeholder:text-gray-400 placeholder:font-medium text-gray-600"
            placeholder="Enter custom category"
            required
          />
        </div>
      )}

      {/* Dietary Preference Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Dietary Preference</label>
        <div className="grid grid-cols-3 gap-4">
          <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-green-400 transition-colors">
            <input
              type="radio"
              name="dietaryPreference"
              value="veg"
              checked={formData.dietaryPreference === 'veg'}
              onChange={onInputChange}
              className="sr-only"
            />
            <div className="flex items-center gap-3 w-full">
              <div className={`w-4 h-4 rounded-full border-2 ${formData.dietaryPreference === 'veg' ? 'border-green-500 bg-green-500' : 'border-gray-300'} flex items-center justify-center`}>
                {formData.dietaryPreference === 'veg' && <div className="w-2 h-2 bg-white rounded-full"></div>}
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-green-400 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">🟢</span>
                </div>
                <span className="text-sm font-medium text-gray-700">Veg</span>
              </div>
            </div>
          </label>

          <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-red-400 transition-colors">
            <input
              type="radio"
              name="dietaryPreference"
              value="non-veg"
              checked={formData.dietaryPreference === 'non-veg'}
              onChange={onInputChange}
              className="sr-only"
            />
            <div className="flex items-center gap-3 w-full">
              <div className={`w-4 h-4 rounded-full border-2 ${formData.dietaryPreference === 'non-veg' ? 'border-red-500 bg-red-500' : 'border-gray-300'} flex items-center justify-center`}>
                {formData.dietaryPreference === 'non-veg' && <div className="w-2 h-2 bg-white rounded-full"></div>}
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-red-400 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">🔴</span>
                </div>
                <span className="text-sm font-medium text-gray-700">Non-Veg</span>
              </div>
            </div>
          </label>

          <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:border-yellow-400 transition-colors">
            <input
              type="radio"
              name="dietaryPreference"
              value="egg"
              checked={formData.dietaryPreference === 'egg'}
              onChange={onInputChange}
              className="sr-only"
            />
            <div className="flex items-center gap-3 w-full">
              <div className={`w-4 h-4 rounded-full border-2 ${formData.dietaryPreference === 'egg' ? 'border-yellow-500 bg-yellow-500' : 'border-gray-300'} flex items-center justify-center`}>
                {formData.dietaryPreference === 'egg' && <div className="w-2 h-2 bg-white rounded-full"></div>}
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">🟡</span>
                </div>
                <span className="text-sm font-medium text-gray-700">Egg</span>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Single Price Field - Show when no weight options */}
      {formData.weightOptions.length === 0 && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Price (₹)</label>
          <input
            type="number"
            name="singlePrice"
            value={formData.singlePrice || ''}
            onChange={onInputChange}
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-orange-400 focus:outline-none transition-colors placeholder:text-gray-400 placeholder:font-medium text-gray-600"
            placeholder="0.00"
            min="0"
            step="0.01"
            required
          />
        </div>
      )}

      {/* Weight Options Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <label className="block text-sm font-medium text-gray-700">Weight Options & Pricing</label>
          <button
            type="button"
            onClick={() => {
              const newWeightOptions = [...formData.weightOptions, { weight: '', price: '', isCustom: false }];
              const event = { target: { name: 'weightOptions', value: newWeightOptions } };
              onInputChange(event);
            }}
            className="flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700 font-medium"
          >
            <Plus className="w-4 h-4" />
            Add Weight Option
          </button>
        </div>

        {formData.weightOptions.map((option, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Weight</label>
              <select
                value={option.isCustom ? 'Custom' : option.weight}
                onChange={(e) => {
                  const newWeightOptions = [...formData.weightOptions];
                  if (e.target.value === 'Custom') {
                    newWeightOptions[index] = { weight: '', price: '', isCustom: true };
                  } else {
                    newWeightOptions[index] = { weight: e.target.value, price: option.price, isCustom: false };
                  }
                  const event = { target: { name: 'weightOptions', value: newWeightOptions } };
                  onInputChange(event);
                }}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-orange-400 focus:outline-none"
              >
                <option value="">Select Weight</option>
                {WEIGHT_OPTIONS.map((weightOption) => (
                  <option key={weightOption.value} value={weightOption.value}>
                    {weightOption.label}
                  </option>
                ))}
                <option value="Custom">Custom</option>
              </select>
              {option.isCustom && (
                <input
                  type="text"
                  value={option.weight}
                  onChange={(e) => {
                    const newWeightOptions = [...formData.weightOptions];
                    newWeightOptions[index].weight = e.target.value;
                    const event = { target: { name: 'weightOptions', value: newWeightOptions } };
                    onInputChange(event);
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-orange-400 focus:outline-none mt-2"
                  placeholder="Enter custom quantity (e.g., Half Sheet, Party Pack)"
                />
              )}
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Price (₹)</label>
              <input
                type="number"
                value={option.price}
                onChange={(e) => {
                  const newWeightOptions = [...formData.weightOptions];
                  newWeightOptions[index].price = e.target.value;
                  const event = { target: { name: 'weightOptions', value: newWeightOptions } };
                  onInputChange(event);
                }}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:border-orange-400 focus:outline-none"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={() => {
                  const newWeightOptions = formData.weightOptions.filter((_, i) => i !== index);
                  const event = { target: { name: 'weightOptions', value: newWeightOptions } };
                  onInputChange(event);
                }}
                className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm font-medium"
              >
                <X className="w-4 h-4" />
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

{/* Image Upload Section */}
<div className="space-y-6">
  <label className="block text-sm font-medium text-gray-700">Item Images</label>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
    {/* Single cover image (first image) */}
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Cover Image</label>
      <input type="file" accept="image/*" onChange={onImageChange} className="hidden" id="image-upload" />
      <label htmlFor="image-upload" className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-orange-400 transition-colors">
        <Upload className="w-8 h-8 text-gray-400 mb-2" />
        <span className="text-sm text-gray-500">Click to upload cover image</span>
      </label>
      {imagePreview && (
        <div className="relative w-full h-full rounded-xl overflow-hidden border-2 border-gray-200 group">
          <img src={imagePreview} alt="Preview" className="w-full max-h-40 object-contain" />
          <button
            type="button"
            onClick={onClearCover}
            className="absolute top-2 right-2 bg-white/90 text-red-600 rounded-full w-7 h-7 flex items-center justify-center shadow transition-opacity"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>

    {/* Multiple images uploader */}
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Additional Images (optional)</label>
      <input type="file" accept="image/*" multiple onChange={onImagesChange} className="hidden" id="images-upload" />
      <label htmlFor="images-upload" className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-orange-400 transition-colors">
        <Upload className="w-8 h-8 text-gray-400 mb-2" />
        <span className="text-sm text-gray-500">Select multiple images </span>
      </label>
      {(Array.isArray(formData.existingImageUrls) && formData.existingImageUrls.length > 0) || (Array.isArray(formData.images) && formData.images.length > 0) ? (
        <div className="grid grid-cols-3 gap-2">
          {Array.isArray(formData.existingImageUrls) && formData.existingImageUrls.map((url, idx) => (
            <div key={`existing-${idx}`} className="relative w-full h-20 rounded-lg overflow-hidden border group">
              <img src={url} alt={`Existing ${idx + 1}`} className="w-full h-full object-cover" />
              <button type="button" onClick={() => {
                const filtered = formData.existingImageUrls.filter((_, i) => i !== idx);
                onInputChange({ target: { name: 'existingImageUrls', value: filtered } });
              }} className="absolute top-1 right-1 bg-white/90 text-red-600 rounded-full w-6 h-6 flex items-center justify-center shadow  transition-opacity">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {Array.isArray(formData.images) && formData.images.map((file, idx) => {
            let preview = '';
            try { preview = URL.createObjectURL(file); } catch (_) { preview = ''; }
            return (
              <div key={`preview-${idx}`} className="relative w-full h-20 rounded-lg overflow-hidden border group">
                {preview ? (
                  <img src={preview} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">No preview</div>
                )}
                <button type="button" onClick={() => {
                  const filtered = formData.images.filter((_, i) => i !== idx);
                  onInputChange({ target: { name: 'images', value: filtered } });
                }} className="absolute top-1 right-1 bg-white/90 text-red-600 rounded-full w-6 h-6 flex items-center justify-center shadow transition-opacity">
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  </div>
</div>




      {/* Form Actions */}
      <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onReset}
          className="px-6 py-3 text-gray-600 border-2 border-gray-300 rounded-xl font-medium hover:border-gray-400 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {editingId ? 'Update Item' : 'Add Item'}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default AddItemForm; 